/**
 * Issue #702 — the burst window for transport failures.
 *
 * A browser that loses connectivity for a few seconds fails every request it
 * has in flight. `reportApiFailure` used to capture each one with Sentry's
 * default fingerprint (the caller's stack), so ONE interruption became one
 * Sentry issue per call site: 8 issues in 12 s on 2026-09-03, 7 in 32 s the day
 * before, with three arriving as REGRESSION alerts because they had been
 * resolved earlier. stg2's nginx served 77x200 / 61x304 / 0x5xx across that
 * minute — the server was healthy throughout.
 *
 * This module holds the "have we already reported this interruption" mark. It
 * is deliberately a module-level timestamp rather than a counter or a set of
 * paths: the thing being deduplicated is the INTERRUPTION, and every path that
 * fails inside it is the same event. Same shape as `teardownState`, and for the
 * same reason — the discriminator is app state, not the error message.
 */

/**
 * How long one reported interruption speaks for the ones behind it.
 *
 * Sized from the two measured bursts: 12 s (2026-09-03, 8 fingerprints) and
 * 32 s (2026-09-02, 7 fingerprints). 45 s covers both with margin. Pollers keep
 * failing inside that window (client-profile at 20 s, notifications at 30 s);
 * captures resume only after the window expires, so a sustained outage still
 * produces a fresh event about once per window rather than falling silent.
 */
export const TRANSPORT_BURST_WINDOW_MS = 45000;

let lastReportedAtMs = null;

/**
 * True when a transport failure was already CAPTURED within the window.
 *
 * The lower bound (`elapsed >= 0`) is not decoration, and it is the same
 * failure direction `teardownState` guards: a system-clock step backwards would
 * otherwise make a long-past mark read as current, suppressing a real outage —
 * the one direction that loses events.
 */
export function isTransportBurstInFlight(nowMs = Date.now()) {
  if (lastReportedAtMs === null) return false;
  const elapsed = nowMs - lastReportedAtMs;
  return elapsed >= 0 && elapsed <= TRANSPORT_BURST_WINDOW_MS;
}

/**
 * Record that a transport failure has been captured, opening the window.
 *
 * Called ONLY where a capture actually happens. A teardown-killed failure and
 * an always-report-route failure must not open a window they did not report
 * through: the first would let a sign-out silence the next genuine failure, and
 * the second would let /register's own (correctly captured) failure suppress a
 * sibling.
 */
export function markTransportReported(nowMs = Date.now()) {
  lastReportedAtMs = nowMs;
}

/** Test seam — lets a spec assert from a known state, as resetTeardownState does. */
export function resetTransportBurstForTests() {
  lastReportedAtMs = null;
}
